<template>
    <div class="comment-vote-actions" aria-label="Comment voting">
        <button
            class="comment-vote-button"
            :class="{ selected: currentVote === 'Up' }"
            type="button"
            :aria-label="`Upvote comment. Current score ${props.voteCount}`"
            v-tooltip="'Upvote'"
            :disabled="props.disabled"
            @click="emitVote('Up')"
        >
            <font-awesome-icon :icon="['fas', 'arrow-up']" />
        </button>
        <span class="comment-vote-score">{{ props.voteCount }}</span>
        <button
            class="comment-vote-button"
            :class="{ selected: currentVote === 'Down' }"
            type="button"
            :aria-label="`Downvote comment. Current score ${props.voteCount}`"
            v-tooltip="'Downvote'"
            :disabled="props.disabled"
            @click="emitVote('Down')"
        >
            <font-awesome-icon :icon="['fas', 'arrow-down']" />
        </button>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { TDocumentCommentVote } from '@/interfaces/deltaGpt/IDocumentComment.ts';

const props = withDefaults(defineProps<{
    voteCount: number;
    currentUserVote?: TDocumentCommentVote | null;
    disabled?: boolean;
}>(), {
    currentUserVote: null,
    disabled: false,
});

const emit = defineEmits<{
    vote: [TDocumentCommentVote];
}>();

const currentVote = computed<TDocumentCommentVote | null>(() => normalizeVote(props.currentUserVote));

function emitVote (vote: TDocumentCommentVote): void {
    if (props.disabled) return;

    emit('vote', vote);
}

function normalizeVote (vote: TDocumentCommentVote | string | null | undefined): TDocumentCommentVote | null {
    if (vote === 'Up' || vote === 'Down') return vote;
    if (vote?.toLowerCase() === 'up') return 'Up';
    if (vote?.toLowerCase() === 'down') return 'Down';

    return null;
}
</script>

<style scoped lang="scss">
.comment-vote-actions {
    display: inline-flex;
    align-items: center;
    gap: calc(var(--smallSpacing) / 2);
}

.comment-vote-button {
    display: inline-flex;
    align-items: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    justify-content: center;
    border: 0;
    background: transparent;
    color: var(--neutral700);
    cursor: pointer;

    &.selected,
    &:hover,
    &:focus-visible {
        color: var(--actionColour);
        outline: none;
    }

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
}

.comment-vote-score {
    min-width: 1.5rem;
    color: var(--neutral800);
    font-size: var(--smallFontSize);
    font-weight: 700;
    text-align: center;
}
</style>
